import React, { useState, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { InitialStateUIType, InitialStateUserType } from "../../typings/state";
import './riasec-test.scss';
import { useBreadcrumbSetter } from "../../hooks/useBreadcrumbSetter";
import { riasecService } from "../../services/riasec.service";
import {
  RiasecQuestion,
  RiasecAnswer,
  RiasecLastTestResponse,
  RiasecProfession
} from "../../typings/riasec";
import RiasecTestIntro from "../../components/Riasec/RiasecTestIntro";
import RiasecTestQuestion from "../../components/Riasec/RiasecTestQuestion";
import RiasecTestResult from "../../components/Riasec/RiasecTestResult";

interface RiasecTestProps extends RouteComponentProps {
  user: InitialStateUserType;
  UI: InitialStateUIType;
}

const RiasecTest: React.FC<RiasecTestProps> = () => {
  const [lastTest, setLastTest] = useState<RiasecLastTestResponse | null>(null);
  const [questions, setQuestions] = useState<RiasecQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: boolean }>({});
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [testResult, setTestResult] = useState<RiasecLastTestResponse | null>(null);
  const [professions, setProfessions] = useState<RiasecProfession[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAnswersSummary, setShowAnswersSummary] = useState(false);

  const breadcrumbs = useMemo(() => [
    { label: 'RIASEC Test', isLast: true }
  ], []);

  useBreadcrumbSetter(breadcrumbs);

  useEffect(() => {
    loadLastTest();
    loadProfessions();
  }, []);

  const loadLastTest = async () => {
    try {
      const response = await riasecService.getLastTest();
      if(response?.status){
        setLastTest(response as RiasecLastTestResponse);
      }
    } catch (error) {
      console.error('Error loading last test:', error);
    }
  };

  const loadProfessions = async () => {
    try {
      const response = await riasecService.getAllProfessions();
      setProfessions(response.data);
    } catch (error) {
      console.error('Error loading professions:', error);
    }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const response = await riasecService.getAllQuestions(true);
      setQuestions(response.data);
      setIsTestStarted(true);
      setCurrentQuestionIndex(0);
      setAnswers({});
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: number, answer: boolean) => {
    // Prevent multiple clicks during transition
    if (loading) return;
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Set loading state and auto-advance to next question after a short delay
    setLoading(true);
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      }
      setLoading(false);
    }, 500);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const answersArray: RiasecAnswer[] = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: Number.parseInt(questionId, 10),
        answer
      }));

      const response = await riasecService.submitTest({ answers: answersArray });
      setTestResult(response);
      setIsTestCompleted(true);
    } catch (error) {
      console.error('Error submitting test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    setIsTestStarted(false);
    setIsTestCompleted(false);
    setTestResult(null);
    loadLastTest();
  };

  const handleViewLastTestDetail = () => {
    setTestResult(lastTest);
    setIsTestCompleted(true);
  };

  const handleToggleSummary = (show: boolean) => {
    setShowAnswersSummary(show);
  };

  if (isTestCompleted && testResult) {
    return (
      <RiasecTestResult
        testResult={testResult}
        professions={professions}
        onBackToHome={handleBackToHome}
      />
    );
  }

  if (isTestStarted && questions.length > 0) {
    return (
      <RiasecTestQuestion
        questions={questions}
        currentQuestionIndex={currentQuestionIndex}
        answers={answers}
        loading={loading}
        showAnswersSummary={showAnswersSummary}
        onAnswer={handleAnswer}
        onNext={handleNext}
        onPrev={handlePrev}
        onSubmit={handleSubmit}
        onToggleSummary={handleToggleSummary}
      />
    );
  }

  return (
    <RiasecTestIntro
      loading={loading}
      lastTest={lastTest}
      professions={professions}
      onStartTest={startTest}
      onViewLastTestDetail={handleViewLastTestDetail}
    />
  );
};

export default RiasecTest;